/**
 * ContactDetailPage Component
 *
 * Full-screen contact detail view following TaskDetailPage pattern.
 * Uses URL params for contact ID and active tab.
 * Supports keyboard navigation (ESC to close).
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { useContactDetail } from './hooks';
import {
  ProfileTab,
  EmailInsightsTab,
  KnowledgeTab,
  CommunicationTab,
} from './tabs';
import { SuppressionModal, BlacklistModal } from './modals';
import MergeModal from '@/components/modules/MergeModal';
import type { ContactDetailTab, SuppressionModalState, BlacklistModalState } from './types';
import { getInitials } from './utils';

const TAB_IDS: ContactDetailTab[] = ['profile', 'email-insights', 'knowledge', 'communication'];

export default function ContactDetailPage() {
  const { t } = useTranslation('people');
  const { contactId, tab } = useParams<{ contactId: string; tab?: string }>();
  const navigate = useNavigate();
  const activeTab = (tab as ContactDetailTab) || 'profile';

  // Contact data hook
  const {
    contact,
    loading,
    error: loadError,
    insights,
    insightsLoading,
    knowledge,
    knowledgeLoading,
    communication,
    communicationLoading,
    suppressedItems,
    refresh,
    refreshInsights,
    refreshKnowledge,
    // refreshCommunication not used yet - communication is preloaded
  } = useContactDetail(contactId);

  // Local state
  const [error, setError] = useState<string | null>(null);
  const [showMerge, setShowMerge] = useState(false);
  const [suppressionModal, setSuppressionModal] = useState<SuppressionModalState>({
    isOpen: false,
    itemType: null,
    itemContent: '',
    itemId: '',
    itemSource: 'email_insights',
  });
  const [blacklistModal, setBlacklistModal] = useState<BlacklistModalState>({
    isOpen: false,
    entityType: 'contact',
    entityId: '',
    entityName: '',
  });

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showMerge && !suppressionModal.isOpen && !blacklistModal.isOpen) {
        navigate('/people/contacts');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate, showMerge, suppressionModal.isOpen, blacklistModal.isOpen]);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleClose = () => {
    navigate('/people/contacts');
  };

  const handleTabChange = (newTab: ContactDetailTab) => {
    navigate(`/people/contacts/${contactId}/${newTab}`);
  };

  const handleNavigateToCompany = (companyId: string) => {
    navigate(`/people/companies/${companyId}`);
  };

  const handleError = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 5000);
  };

  // Suppression modal handlers
  const handleOpenSuppressionModal = (
    itemType: 'expertise' | 'fact',
    itemContent: string,
    itemId: string,
    itemSource: 'email_insights' | 'knowledge_card'
  ) => {
    setSuppressionModal({
      isOpen: true,
      itemType,
      itemContent,
      itemId,
      itemSource,
    });
  };

  const handleSuppression = async (reason?: string) => {
    if (!contactId || !suppressionModal.itemType) return;
    try {
      const { contactsApi } = await import('@/services/contactsApi');
      if (suppressionModal.itemSource === 'email_insights') {
        // Create a suppression card to mark this item as suppressed
        await contactsApi.addNote(contactId, {
          card_type: 'suppression',
          title: `Suppressed ${suppressionModal.itemType}`,
          content: JSON.stringify({
            item_type: suppressionModal.itemType,
            item_value: suppressionModal.itemContent,
            reason: reason || '',
          }),
        });
      } else {
        await contactsApi.deleteKnowledgeCard(contactId, suppressionModal.itemId);
      }
      refreshKnowledge();
      refreshInsights();
    } catch (err) {
      handleError(err instanceof Error ? err.message : 'Failed to suppress item');
    } finally {
      setSuppressionModal({
        isOpen: false,
        itemType: null,
        itemContent: '',
        itemId: '',
        itemSource: 'email_insights',
      });
    }
  };

  // Blacklist modal handlers
  const handleOpenBlacklistModal = () => {
    if (!contact) return;
    setBlacklistModal({
      isOpen: true,
      entityType: 'contact',
      entityId: contact.id,
      entityName: `${contact.first_name} ${contact.last_name || ''}`.trim(),
    });
  };

  const handleBlacklist = async (reason: string) => {
    if (!contactId) return;
    try {
      const { contactsApi } = await import('@/services/contactsApi');
      await contactsApi.update(contactId, { is_blacklisted: true, blacklist_reason: reason });
      refresh();
    } catch (err) {
      handleError(err instanceof Error ? err.message : 'Failed to blacklist contact');
    } finally {
      setBlacklistModal({
        isOpen: false,
        entityType: 'contact',
        entityId: '',
        entityName: '',
      });
    }
  };

  const handleRemoveFromBlacklist = async () => {
    if (!contactId) return;
    try {
      const { contactsApi } = await import('@/services/contactsApi');
      await contactsApi.update(contactId, { is_blacklisted: false, blacklist_reason: null });
      refresh();
    } catch (err) {
      handleError(err instanceof Error ? err.message : 'Failed to remove from blacklist');
    }
  };

  // Merge handlers
  const handleApproveMerge = async () => {
    if (!contactId || !insights?.candidate_match?.id) return;
    try {
      const { contactsApi } = await import('@/services/contactsApi');
      await contactsApi.approveMerge(contactId, insights.candidate_match.id);
      refresh();
      refreshInsights();
    } catch (err) {
      handleError(err instanceof Error ? err.message : 'Failed to merge contacts');
    }
  };

  const handleRejectMerge = async () => {
    if (!contactId) return;
    try {
      const { contactsApi } = await import('@/services/contactsApi');
      await contactsApi.rejectMerge(contactId);
      refreshInsights();
    } catch (err) {
      handleError(err instanceof Error ? err.message : 'Failed to reject merge');
    }
  };

  const handleDeleteContact = async () => {
    if (!contactId) return;
    if (!confirm(t('contact_detail.delete_confirmation'))) return;
    try {
      const { contactsApi } = await import('@/services/contactsApi');
      await contactsApi.delete(contactId);
      navigate('/people/contacts');
    } catch (err) {
      handleError(err instanceof Error ? err.message : 'Failed to delete contact');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Not found
  if (!contact) {
    return (
      <div className="fixed inset-0 z-50 bg-white flex flex-col items-center justify-center">
        <p className="text-gray-500 mb-4">{t('contact_detail.not_found')}</p>
        <button
          onClick={handleClose}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          {t('contact_detail.back_to_contacts')}
        </button>
      </div>
    );
  }

  const contactName = `${contact.first_name} ${contact.last_name || ''}`.trim();

  return (
    <div className="fixed inset-0 z-50 bg-white overflow-hidden flex flex-col">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-gray-200 bg-white">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Close button */}
              <button
                onClick={handleClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title={t('contact_detail.close_esc')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Avatar */}
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-lg font-medium">
                {contact.avatar_url ? (
                  <img src={contact.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  getInitials(contactName)
                )}
              </div>

              {/* Name and title */}
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{contactName}</h1>
                {contact.title && (
                  <p className="text-sm text-gray-500">{contact.title}</p>
                )}
              </div>

              {/* Badges */}
              <div className="flex gap-2">
                {contact.importance && contact.importance !== 'normal' && (
                  <span className={`text-xs px-2 py-1 rounded ${
                    contact.importance === 'vip' ? 'bg-purple-100 text-purple-700' :
                    contact.importance === 'high' ? 'bg-red-100 text-red-700' :
                    contact.importance === 'low' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {t(`contact_detail.badges.${contact.importance}`)}
                  </span>
                )}
                {contact.is_blacklisted && (
                  <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-700">
                    {t('contact_detail.badges.blacklisted')}
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowMerge(true)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {t('contact_detail.actions.merge')}
              </button>
              {!contact.is_blacklisted && (
                <button
                  onClick={handleOpenBlacklistModal}
                  className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {t('contact_detail.actions.blacklist')}
                </button>
              )}
              <button
                onClick={handleDeleteContact}
                className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                {t('contact_detail.actions.delete')}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-4 -mb-4">
            {TAB_IDS.map((tabId) => (
              <button
                key={tabId}
                onClick={() => handleTabChange(tabId)}
                className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === tabId
                    ? 'bg-white text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t(`contact_detail.tabs.${tabId === 'email-insights' ? 'email_insights' : tabId}`)}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Error banner */}
      {(error || loadError) && (
        <div className="flex-shrink-0 px-6 py-3 bg-red-50 border-b border-red-100">
          <p className="text-sm text-red-600">{error || loadError}</p>
        </div>
      )}

      {/* Blacklist banner */}
      {contact.is_blacklisted && (
        <div className="flex-shrink-0 mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="flex-1">
              <h4 className="font-medium text-red-800">{t('contact_detail.blacklist_banner.title')}</h4>
              {contact.blacklist_reason && (
                <p className="text-sm text-red-700 mt-1">
                  {t('contact_detail.blacklist_banner.reason_label')} {contact.blacklist_reason}
                </p>
              )}
              {contact.blacklisted_at && (
                <p className="text-xs text-red-600 mt-1">
                  {t('contact_detail.blacklist_banner.since_label')} {new Date(contact.blacklisted_at).toLocaleDateString()}
                </p>
              )}
            </div>
            <button
              onClick={handleRemoveFromBlacklist}
              className="px-3 py-1.5 text-sm bg-white text-red-600 border border-red-300 rounded hover:bg-red-50 transition-colors"
            >
              {t('contact_detail.blacklist_banner.remove_button')}
            </button>
          </div>
        </div>
      )}

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
        {activeTab === 'profile' && (
          <ProfileTab
            contact={contact}
            onUpdate={refresh}
            onError={handleError}
            onNavigateToCompany={handleNavigateToCompany}
          />
        )}
        {activeTab === 'email-insights' && (
          <EmailInsightsTab
            insights={insights}
            loading={insightsLoading}
            onRefresh={() => refreshInsights()}
            onApproveMerge={handleApproveMerge}
            onRejectMerge={handleRejectMerge}
          />
        )}
        {activeTab === 'knowledge' && (
          <KnowledgeTab
            cards={knowledge}
            loading={knowledgeLoading}
            entityId={contact.id}
            entityType="contact"
            insights={insights}
            suppressedItems={suppressedItems}
            onRefresh={refreshKnowledge}
            onOpenSuppressionModal={handleOpenSuppressionModal}
            onError={handleError}
          />
        )}
        {activeTab === 'communication' && (
          <CommunicationTab
            data={communication}
            loading={communicationLoading}
          />
        )}
      </div>

      {/* Modals */}
      {showMerge && (
        <MergeModal
          isOpen={showMerge}
          onClose={() => setShowMerge(false)}
          entityType="contact"
          sourceEntity={contact}
          onMergeComplete={() => {
            setShowMerge(false);
            navigate('/people/contacts');
          }}
        />
      )}

      <SuppressionModal
        state={suppressionModal}
        onConfirm={handleSuppression}
        onCancel={() => setSuppressionModal({
          isOpen: false,
          itemType: null,
          itemContent: '',
          itemId: '',
          itemSource: 'email_insights',
        })}
      />

      <BlacklistModal
        state={blacklistModal}
        onConfirm={handleBlacklist}
        onCancel={() => setBlacklistModal({
          isOpen: false,
          entityType: 'contact',
          entityId: '',
          entityName: '',
        })}
      />
    </div>
  );
}
